import { Injectable, inject } from '@angular/core';
import { environment } from '@/environments/environment';
import { DifyBaseService } from './dify.base.service';
import { MCPAuthService } from '@/app/services/mcp-auth.service';

/**
 * 通用 AI 助手服务（TVMS 快捷查询）。
 *
 * 面向全体用户的日常查询助手，对接远程 Dify 应用。
 * 复用 DifyBaseService 的通用逻辑，仅配置 API 地址/密钥，inputs 使用默认用户上下文。
 *
 * 使用前需先通过 MCP signin 接口换取 token，并作为 `X_MCP_Signature` 传给 Dify chatflow
 * （见 initialize()）。
 */
@Injectable({ providedIn: 'root' })
export class DifyGeneralService extends DifyBaseService {
  private readonly mcpAuth = inject(MCPAuthService);

  protected readonly apiUrl = environment.difyQuickHelperApiUrl;
  protected readonly apiKey = environment.difyQuickHelperApiKey;

  /**
   * TVMS 快捷查询（通用助手）是否启用。
   * 家用网络访问不到远程 Dify 域名时临时禁用（initialize() 会抛"服务暂不可用"）。
   * 在公司/可访问远程 Dify 的网络下改回 true 即可。
   */
  protected override enabled = true;

  /**
   * 初始化：先调用 MCP signin 拿到 token 并赋给 X_MCP_Signature，
   * 再走基类连接验证 / 加载历史。
   */
  override async initialize(): Promise<void> {
    if (!this.enabled) {
      this.connectionStatus.set('error');
      throw new Error('服务暂不可用');
    }
    if (this.connectionStatus() === 'connected') return;

    // 换取 MCP token 作为 X_MCP_Signature（signin 失败则留空，不阻塞主流程）
    const mcpToken = await this.mcpAuth.signin();
    if (mcpToken) {
      this.userToken.set(mcpToken);
    }

    await super.initialize();
  }
}
